import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AppShell, Icon } from '../components/AppShell';
import { api, ApiError, type RolRij } from '../lib/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../lib/auth';
import { fmtDateTime } from '../lib/format';

/** Foutmelding van de server tonen als die er is (bv. "Deze rol is nog aan gebruikers toegekend"). */
function foutTekst(e: unknown, standaard: string): string {
  if (e instanceof ApiError) {
    const p = e.payload as { error?: unknown } | null;
    if (p && typeof p.error === 'string') return p.error;
  }
  return standaard;
}

const rolLabel = (r: RolRij) => `${r.naam} (niveau ${r.niveau})`;

type NieuweGebruiker = { email: string; displayName: string; role: 'user' | 'admin'; rolId: string; gedeeldAccount: boolean };
const LEGE_GEBRUIKER: NieuweGebruiker = { email: '', displayName: '', role: 'user', rolId: '', gedeeldAccount: false };

export function UsersManager() {
  const qc = useQueryClient();
  const toast = useToast();
  const { user: me } = useAuth();
  const [showNew, setShowNew] = useState(false);
  const [newUser, setNewUser] = useState<NieuweGebruiker>(LEGE_GEBRUIKER);

  const usersQ = useQuery({ queryKey: ['users'], queryFn: () => api.users.list() });
  const rollenQ = useQuery({ queryKey: ['rollen'], queryFn: () => api.users.rollen() });
  const aiUsageQ = useQuery({ queryKey: ['ai-usage'], queryFn: () => api.overview.aiUsage() });
  const users = usersQ.data?.users ?? [];
  const rollen = rollenQ.data?.rollen ?? [];
  const isMe = (u: { id: string; email: string }) => (!!me?.id && u.id === me.id) || (!!me?.email && u.email.toLowerCase() === me.email.toLowerCase());

  const create = useMutation({
    mutationFn: () => api.users.create({ ...newUser, rolId: newUser.rolId || null }),
    onSuccess: () => { toast.success('Gebruiker toegevoegd — laat hen via Toolbox inloggen'); setNewUser(LEGE_GEBRUIKER); setShowNew(false); qc.invalidateQueries({ queryKey: ['users'] }); },
    onError: (e) => toast.error('Aanmaken mislukt: ' + foutTekst(e, e instanceof Error ? e.message : 'fout')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.users.remove(id),
    onSuccess: () => { toast.success('Verwijderd'); qc.invalidateQueries({ queryKey: ['users'] }); },
    onError: (e) => toast.error(foutTekst(e, 'Verwijderen mislukt')),
  });
  const updateUser = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof api.users.update>[1] }) => api.users.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (e) => toast.error(foutTekst(e, 'Update mislukt')),
  });

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="page-title">Gebruikers</h1>
          <p className="page-subtitle">{users.length} accounts</p>
        </div>
        <button className="btn-accent" onClick={() => setShowNew(!showNew)}>+ Nieuwe gebruiker</button>
      </div>

      {/* AI Usage stats */}
      {aiUsageQ.data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <div className="card-flat">
            <div className="text-xs font-medium text-secondary">AI-kosten vandaag</div>
            <div className="mt-1 text-xl font-bold tabular-nums">€ {(aiUsageQ.data.today.cost ?? 0).toFixed(2)}</div>
            <div className="text-xs text-secondary">{aiUsageQ.data.today.count ?? 0} parses</div>
          </div>
          <div className="card-flat">
            <div className="text-xs font-medium text-secondary">Deze maand</div>
            <div className="mt-1 text-xl font-bold tabular-nums">€ {(aiUsageQ.data.thisMonth.cost ?? 0).toFixed(2)}</div>
            <div className="text-xs text-secondary">{aiUsageQ.data.thisMonth.count ?? 0} parses</div>
          </div>
          <div className="card-flat">
            <div className="text-xs font-medium text-secondary">Totaal sinds start</div>
            <div className="mt-1 text-xl font-bold tabular-nums">€ {(aiUsageQ.data.allTime.cost ?? 0).toFixed(2)}</div>
            <div className="text-xs text-secondary">{aiUsageQ.data.allTime.count ?? 0} parses</div>
          </div>
        </div>
      )}

      {showNew && (
        <div className="card-flat mb-5">
          <p className="text-xs text-secondary mb-3">
            Pre-create-by-email: voeg een @recotex.be account toe; bij hun eerste Toolbox SSO-login wordt het automatisch gelinkt.
            Geef meteen een rol, dan gelden de meldingen en rechten vanaf de eerste login.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr_2fr_auto_auto] gap-3 items-end">
            <div>
              <label className="field-label" htmlFor="nieuw-email">E-mailadres</label>
              <input id="nieuw-email" type="email" className="input" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
            </div>
            <div>
              <label className="field-label" htmlFor="nieuw-naam">Naam</label>
              <input id="nieuw-naam" className="input" value={newUser.displayName} onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })} />
            </div>
            <div>
              <label className="field-label" htmlFor="nieuw-toegang" title="Admin ziet de beheerpagina's (prijslijsten, onderaannemers, gebruikers)">Toegang</label>
              <select id="nieuw-toegang" className="select" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value as 'user' | 'admin' })}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="nieuw-rol">Rol</label>
              <select id="nieuw-rol" className="select" value={newUser.rolId} onChange={(e) => setNewUser({ ...newUser, rolId: e.target.value })}>
                <option value="">Geen rol</option>
                {rollen.map((r) => <option key={r.id} value={r.id}>{rolLabel(r)}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none pb-2.5" title="Account dat door meerdere personen gebruikt wordt (bv. calculaties@): de tool vraagt wie er werkt">
              <input type="checkbox" checked={newUser.gedeeldAccount} onChange={(e) => setNewUser({ ...newUser, gedeeldAccount: e.target.checked })} />
              Gedeeld account
            </label>
            <button className="btn-primary" onClick={() => create.mutate()} disabled={!newUser.email || !newUser.displayName || create.isPending}>
              Toevoegen
            </button>
          </div>
        </div>
      )}

      <div className="card overflow-x-auto mb-5">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="thead-row">
              <th className="text-left px-3 py-2">Gebruiker</th>
              <th className="text-left px-3 py-2 w-32" title="Admin ziet de beheerpagina's">Toegang</th>
              <th className="text-left px-3 py-2 w-[340px]" title="Rol in de hiërarchie: bepaalt meldingen, bevestigen, vrijgeven en beheer">Rol</th>
              <th className="text-center px-3 py-2" title="Gedeeld account: de tool vraagt wie er werkt en logt dat mee">Gedeeld</th>
              <th className="text-left px-3 py-2">Laatste login</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-lightgrey last:border-b-0 text-sm">
                <td className="px-3 py-2">
                  <div className="font-semibold">{u.displayName}</div>
                  <div className="text-xs text-secondary break-all">{u.email}</div>
                </td>
                <td className="px-3 py-2">
                  <select className="select bg-transparent border-transparent hover:border-border min-w-[112px]" value={u.role}
                    aria-label={`Toegang ${u.displayName}`}
                    onChange={(e) => updateUser.mutate({ id: u.id, data: { role: e.target.value as 'admin' | 'user' } })}
                    disabled={isMe(u)}>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td className="px-3 py-2">
                  <select className="select bg-transparent border-transparent hover:border-border min-w-[320px]" value={u.rolId ?? ''}
                    aria-label={`Rol ${u.displayName}`}
                    onChange={(e) => updateUser.mutate({ id: u.id, data: { rolId: e.target.value || null } })}>
                    <option value="">Geen rol</option>
                    {rollen.map((r) => <option key={r.id} value={r.id}>{rolLabel(r)}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2 text-center">
                  <input type="checkbox" checked={u.gedeeldAccount} aria-label={`Gedeeld account ${u.displayName}`}
                    onChange={(e) => updateUser.mutate({ id: u.id, data: { gedeeldAccount: e.target.checked } })} />
                </td>
                <td className="px-3 py-2 text-sm text-secondary whitespace-nowrap">{u.lastLoginAt ? fmtDateTime(u.lastLoginAt) : '—'}</td>
                <td className="px-3 py-2">
                  {!isMe(u) && (
                    <button className="btn-ghost btn-sm px-2" aria-label={`Verwijder ${u.email}`} title="Verwijderen" onClick={() => { if (confirm(`Gebruiker "${u.email}" verwijderen?`)) remove.mutate(u.id); }}><Icon name="sluiten" size={16} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <RollenBlok rollen={rollen} loading={rollenQ.isLoading} gebruikersPerRol={(id) => users.filter((u) => u.rolId === id).length} />
    </AppShell>
  );
}

type NieuweRol = Omit<RolRij, 'id'>;
const LEGE_ROL: NieuweRol = { naam: '', niveau: 3, magBevestigen: true, magVrijgeven: true, magBeheren: false };

/** Rollen met niveau (SPEC 27okt 7). Enkel admins komen op deze pagina; de server laat beheer van rollen enkel aan admins toe. */
function RollenBlok({ rollen, loading, gebruikersPerRol }: { rollen: RolRij[]; loading: boolean; gebruikersPerRol: (rolId: string) => number }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [nieuw, setNieuw] = useState<NieuweRol>(LEGE_ROL);
  const vernieuw = () => qc.invalidateQueries({ queryKey: ['rollen'] });

  const maak = useMutation({
    mutationFn: () => api.users.maakRol({ ...nieuw, naam: nieuw.naam.trim() }),
    onSuccess: () => { toast.success('Rol toegevoegd'); setNieuw(LEGE_ROL); vernieuw(); },
    onError: (e) => toast.error(foutTekst(e, 'Rol toevoegen mislukt')),
  });
  const wijzig = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<NieuweRol> }) => api.users.wijzigRol(id, data),
    onSuccess: vernieuw,
    onError: (e) => { toast.error(foutTekst(e, 'Rol wijzigen mislukt')); vernieuw(); },
  });
  const verwijder = useMutation({
    mutationFn: (id: string) => api.users.verwijderRol(id),
    onSuccess: () => { toast.success('Rol verwijderd'); vernieuw(); },
    onError: (e) => toast.error(foutTekst(e, 'Rol verwijderen mislukt')),
  });

  const rechten: Array<{ key: 'magBevestigen' | 'magVrijgeven' | 'magBeheren'; label: string; uitleg: string }> = [
    { key: 'magBevestigen', label: 'Bevestigen', uitleg: 'Mag de pop-ups in een calculatie bevestigen' },
    { key: 'magVrijgeven', label: 'Vrijgeven', uitleg: 'Mag posten die open staan voor oplevering vrijgeven' },
    { key: 'magBeheren', label: 'Beheren', uitleg: 'Mag labels, prijslijsten, standaardprijzen, prijslijstversies en geleerde regels beheren' },
  ];
  const niveauGeldig = (n: number) => Number.isInteger(n) && n >= 1 && n <= 9;

  return (
    <div className="card">
      <h2 className="text-base font-semibold mb-1">Rollen</h2>
      <p className="text-sm text-secondary mb-4">
        Meldingen gaan naar alle niveaus boven de invoerder; niveau 1 is het hoogste.
      </p>
      {loading ? (
        <p className="text-sm text-secondary">Laden…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="thead-row">
                <th className="text-left px-3 py-2">Naam</th>
                <th className="text-left px-3 py-2 w-24">Niveau</th>
                {rechten.map((r) => <th key={r.key} className="text-center px-3 py-2 w-28" title={r.uitleg}>{r.label}</th>)}
                <th className="text-right px-3 py-2 w-28">Gebruikers</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {rollen.map((r) => {
                const aantal = gebruikersPerRol(r.id);
                return (
                  <tr key={r.id} className="border-b border-lightgrey text-sm">
                    <td className="px-3 py-1">
                      <input className="w-full bg-transparent py-1 font-semibold hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm"
                        defaultValue={r.naam} key={r.id + '-naam-' + r.naam} aria-label={`Naam rol ${r.naam}`}
                        onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== r.naam) wijzig.mutate({ id: r.id, data: { naam: v } }); else e.target.value = r.naam; }} />
                    </td>
                    <td className="px-3 py-1">
                      <input type="number" min={1} max={9} className="w-16 bg-transparent py-1 tabular-nums hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm"
                        defaultValue={r.niveau} key={r.id + '-niveau-' + r.niveau} aria-label={`Niveau rol ${r.naam}`}
                        onBlur={(e) => { const n = parseInt(e.target.value, 10); if (niveauGeldig(n) && n !== r.niveau) wijzig.mutate({ id: r.id, data: { niveau: n } }); else e.target.value = String(r.niveau); }} />
                    </td>
                    {rechten.map((recht) => (
                      <td key={recht.key} className="px-3 py-1 text-center">
                        <input type="checkbox" checked={r[recht.key]} aria-label={`${recht.label} voor ${r.naam}`}
                          onChange={(e) => wijzig.mutate({ id: r.id, data: { [recht.key]: e.target.checked } })} />
                      </td>
                    ))}
                    <td className="px-3 py-1 text-right tabular-nums text-sm text-secondary">{aantal}</td>
                    <td className="px-3 py-1 text-right">
                      <button className="btn-ghost btn-sm px-2" aria-label={`Verwijder rol ${r.naam}`}
                        disabled={aantal > 0 || verwijder.isPending}
                        title={aantal > 0 ? 'Nog aan gebruikers toegekend: geef hen eerst een andere rol' : 'Rol verwijderen'}
                        onClick={() => { if (confirm(`Rol "${r.naam}" verwijderen?`)) verwijder.mutate(r.id); }}><Icon name="sluiten" size={16} /></button>
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td className="px-3 pt-3">
                  <input className="input" placeholder="Nieuwe rol, bv. Werfleider" value={nieuw.naam} aria-label="Naam nieuwe rol"
                    onChange={(e) => setNieuw({ ...nieuw, naam: e.target.value })} />
                </td>
                <td className="px-3 pt-3">
                  <input type="number" min={1} max={9} className="input w-20 tabular-nums" value={nieuw.niveau} aria-label="Niveau nieuwe rol"
                    onChange={(e) => setNieuw({ ...nieuw, niveau: parseInt(e.target.value, 10) || 0 })} />
                </td>
                {rechten.map((recht) => (
                  <td key={recht.key} className="px-3 pt-3 text-center">
                    <input type="checkbox" checked={nieuw[recht.key]} aria-label={`${recht.label} voor nieuwe rol`}
                      onChange={(e) => setNieuw({ ...nieuw, [recht.key]: e.target.checked })} />
                  </td>
                ))}
                <td colSpan={2} className="px-3 pt-3 text-right">
                  <button className="btn-primary btn-sm" disabled={!nieuw.naam.trim() || !niveauGeldig(nieuw.niveau) || maak.isPending} onClick={() => maak.mutate()}>
                    + Rol toevoegen
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
